// Astrea Ops Platform - Database tab data-access layer (organisations/venues/vendors)
//
// Phase 3, first slice. The app's existing `database` array shape (id, type,
// name, category, contact, phone, email, city, state, location, notes, rating)
// stays exactly as every tab already consumes it - only the load/save boundary
// changes, from the old localStorage blob to real per-row Supabase reads and
// writes, one row per create/update/delete rather than dumping the whole array
// on every change. See claude/go-live-plan.md (Phase 3) and
// claude/phase1-schema-migrations.md for the schema this maps onto.
//
// Ids: Supabase's three tables default to gen_random_uuid(), but the app needs
// the new id synchronously the moment a record is created - it's often used
// immediately (e.g. project.clientId = e.id) before any network round trip
// completes - so every new entry gets a real UUID assigned client-side with
// crypto.randomUUID() and sent explicitly on insert, rather than waiting on
// Supabase's default and reconciling the id afterward. App.jsx's own uid()
// (a short Math.random() string) is not a valid Postgres uuid, so the six call
// sites that create new database entries were switched to crypto.randomUUID()
// alongside this file.
//
// organisations has no contact/phone/email columns of its own - Phase 1 pushed
// that into the richer `contacts` table (organisation_id-linked), so one
// organisation can carry several named contacts with roles. The Database tab
// still shows a single simple contact field per client, though, so a client
// entry's contact/phone/email is kept in sync with one contacts row per
// organisation marked role_type='primary': created the first time a client
// entry gets a contact, updated in place after that, and left alone otherwise -
// this never touches any other contacts on the same organisation (for example
// ones Phase 5's monday.com import already created with a different role_type).
//
// venues and vendors both keep their own contact/phone/email/location columns
// directly (no linked-contacts indirection needed for those two).
//
// v7.97 (Martin, 19 Oct 2026): "For our clients in the database, I would like the ability to enter
// multiple contacts (at least up to 5) as well as the main number." A client entry now carries
// `contacts` (every active contacts row for the organisation, the primary one first: id, name,
// title, email, phone) and its own main phone and email (organisations.phone / .email, added by
// 20261019000001_client_contacts_main_phone.sql). `contact` stays as the first contact's name, for
// the places that show one name. Saving a client writes the whole list: rows are updated or added,
// and a contact removed from the list is marked inactive, never deleted.
//
// v7.99 (Martin, 20 Oct 2026): "Please add the multiple contacts for the vendors and venues, the
// same as you did for the clients." Contacts rows can now belong to a venue (venue_id) or a vendor
// (vendor_id) as well, added by 20261020000001_venue_vendor_contacts_categories.sql, which also
// turns each venue's and vendor's single contact into its first contacts row. Their phone and
// email stay as the record's own main phone and email, and the contact column keeps the first
// contact's name. Before that migration has run, venue and vendor saves leave the list out.

// v7.74 (16 Oct 2026): every entry also carries a street address (the old "Location" box, which
// mostly repeated the city and state, is no longer shown), a country (default USA) and a website.
// Vendor compliance (COI, licence, W-9, insurance limit) and venue attributes (capacity and so on)
// had boxes on the Database tab but were never saved to their columns; they are now. Phones and
// emails are put into the one house format (src/lib/contactFormat.js) on the way in.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';
import { normalizeEmail, formatPhoneField, tidyText } from './contactFormat';

const txt = v => { const s = String(v ?? '').trim(); return s || null; };
const dateOrNull = v => (/^\d{4}-\d{2}-\d{2}$/.test(String(v || '')) ? v : null);
const numOrNull = v => { if (v === '' || v == null) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const COUNTRY_DEFAULT = 'USA';
export const MAX_CLIENT_CONTACTS = 8;
export const MAX_CONTACTS = MAX_CLIENT_CONTACTS;
// The contacts column that links a contacts row to each kind of record
const OWNER_COL = { client: 'organisation_id', venue: 'venue_id', vendor: 'vendor_id' };
const NO_NAME = '(no name given)';
const newId = () => (globalThis.crypto && globalThis.crypto.randomUUID ? globalThis.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16); }));
const sameName = (a, b) => String(a || '').trim().toLowerCase().replace(/\s+/g, ' ') === String(b || '').trim().toLowerCase().replace(/\s+/g, ' ');

// "Name, Title" or "Name - Title" as a contact
const splitContact = one => { const m = one.match(/^(.+?)(?:,| - )\s*(.+)$/); return { id: '', name: m ? m[1].trim() : one, title: m ? m[2].trim() : '', email: '', phone: '' }; };
// A record's contacts as they should be saved: tidied, empty rows dropped, every one with an id.
// A record with no contacts list yet (one from before v7.97, or from code that only knows the
// single contact) has its single `contact` as the list, split into name and title.
// v7.99: only then. A list that leaves the single contact out (it was removed) is kept as it is.
export function entityContactsOf(entry, country) {
  const c0 = txt(country) || txt(entry && entry.country) || COUNTRY_DEFAULT;
  const list = (Array.isArray(entry && entry.contacts) ? entry.contacts : []).map(c => ({
    id: (c && c.id) || '',
    name: tidyText(String((c && c.name) || '').trim()),
    title: String((c && c.title) || '').trim(),
    email: normalizeEmail(String((c && c.email) || '')),
    phone: formatPhoneField(String((c && c.phone) || ''), { country: c0 }),
  })).filter(c => c.name || c.email || c.phone);
  const one = String((entry && entry.contact) || '').trim();
  if (one && !list.length) list.push(splitContact(one));
  list.forEach(c => { if (!c.id) c.id = newId(); });
  return list.slice(0, 20);
}
export const clientContactsOf = entityContactsOf;
// A contact found by Look up online ("Name, Title") added to a record's list, unless already there
export function addContactTo(entry, raw) {
  const list = entityContactsOf(entry);
  const one = String(raw || '').trim();
  if (!one) return list;
  const c = splitContact(one);
  if (list.some(x => sameName(x.name, c.name) || sameName(x.name, one))) return list;
  c.id = newId();
  return [...list, c];
}

// The entry as it should be saved: emails in lower case, phone numbers in the house format
// (national numbers read in the entry's country), numbers and emails in the contact line and notes
// tidied. Returns the same object when nothing needed changing.
export function cleanDbEntry(entry) {
  if (!entry) return entry;
  const out = { ...entry };
  const country = txt(entry.country) || COUNTRY_DEFAULT;
  out.country = country;
  if (typeof entry.email === 'string') out.email = normalizeEmail(entry.email);
  if (typeof entry.phone === 'string') out.phone = formatPhoneField(entry.phone, { country });
  if (typeof entry.contact === 'string') out.contact = tidyText(entry.contact);
  if (typeof entry.notes === 'string') out.notes = tidyText(entry.notes);
  if (typeof entry.website === 'string') out.website = entry.website.trim();
  if (typeof entry.street === 'string') out.street = entry.street.trim();
  if (OWNER_COL[entry.type]) {
    out.contacts = entityContactsOf(entry, country);
    out.contact = out.contacts[0] ? out.contacts[0].name : '';
  }
  return out;
}

// Vendor compliance and venue attribute boxes: app field -> column
const VENDOR_EXTRA = { coiExpiry: 'coi_expiry', licenseNumber: 'license_number', licenseState: 'license_state', w9Date: 'w9_on_file_date', insuranceLimit: 'insurance_limit' };
const VENUE_EXTRA = { capacity: 'capacity', functionRooms: 'function_rooms', indoorOutdoor: 'indoor_outdoor', minutesToHospital: 'minutes_to_hospital',
  unionLabor: 'union_labor', inHouseSecurityRequired: 'in_house_security_required', exclusiveVendors: 'exclusive_vendors' };
function extraFromRow(row, map) {
  const out = {};
  Object.entries(map).forEach(([f, col]) => { out[f] = row[col] == null ? '' : String(row[col]); });
  return out;
}
function extraToRow(entry, map) {
  const out = {};
  Object.entries(map).forEach(([f, col]) => {
    if (!(f in entry)) return; // not loaded or not shown: leave the column as it is
    out[col] = col === 'coi_expiry' || col === 'w9_on_file_date' ? dateOrNull(entry[f]) : col === 'minutes_to_hospital' ? numOrNull(entry[f]) : txt(entry[f]);
  });
  return out;
}
// The columns every vendor and venue row shares.
function venueVendorRow(e) {
  return {
    name: e.name,
    category: e.category || null,
    contact: e.contact || null,
    phone: e.phone || null,
    email: e.email || null,
    address_line1: txt(e.street),
    city: e.city || null,
    state: e.state || null,
    country: txt(e.country) || COUNTRY_DEFAULT,
    website: txt(e.website),
    location: e.location || null,
    rating: e.rating || null,
    notes: e.notes || null,
  };
}
function clientRow(e) {
  return {
    name: e.name,
    phone: txt(e.phone),
    email: txt(e.email),
    category: e.category || null,
    address_line1: txt(e.street),
    city: e.city || null,
    state: e.state || null,
    country: txt(e.country) || COUNTRY_DEFAULT,
    website: txt(e.website),
    notes: e.notes || null,
    // v7.109: the client's usual Senior Manager and Finance person (people ids)
    usual_admin_id: e.usualAdminId || null,
    usual_finance_id: e.usualFinanceId || null,
  };
}

// contactRows: the organisation's active contacts rows, in order (see loadDatabaseEntries)
function orgToEntry(org, contactRows) {
  const contacts = (contactRows || []).map(c => ({
    id: c.id, name: c.name === NO_NAME ? '' : (c.name || ''), title: c.title || '', email: c.email || '', phone: c.phone || '',
  }));
  return {
    id: org.id,
    type: 'client',
    name: org.name || '',
    category: org.category || '',
    contact: contacts[0] ? contacts[0].name : '',
    contacts,
    // v7.97: the organisation's own main number and email (no longer the primary contact's)
    phone: org.phone || '',
    email: org.email || '',
    street: org.address_line1 || '',
    city: org.city || '',
    state: org.state || '',
    country: org.country || COUNTRY_DEFAULT,
    website: org.website || '',
    location: [org.city, org.state].filter(Boolean).join(', '),
    notes: org.notes || '',
    usualAdminId: org.usual_admin_id || '',
    usualFinanceId: org.usual_finance_id || '',
    rating: 'unrated', // organisations don't carry a rating column - only vendors and venues do
    projects: [],
  };
}

// contactRows as for orgToEntry. Before the v7.99 migration there are none: the single contact
// column shows as the one contact.
function contactsFromRows(rows, single) {
  const list = (rows || []).map(c => ({ id: c.id, name: c.name === NO_NAME ? '' : (c.name || ''), title: c.title || '', email: c.email || '', phone: c.phone || '' }));
  const one = String(single || '').trim();
  if (!list.length && one) list.push(splitContact(one));
  return list;
}
function venueToEntry(v, contactRows) {
  const contacts = contactsFromRows(contactRows, v.contact);
  return {
    id: v.id,
    type: 'venue',
    name: v.name || '',
    category: v.category || '',
    contact: contacts[0] ? contacts[0].name : '',
    contacts,
    phone: v.phone || '',
    email: v.email || '',
    street: v.address_line1 || '',
    city: v.city || '',
    state: v.state || '',
    country: v.country || COUNTRY_DEFAULT,
    website: v.website || '',
    location: v.location || [v.city, v.state].filter(Boolean).join(', '),
    notes: v.notes || '',
    rating: v.rating || 'unrated',
    projects: [],
    ...extraFromRow(v, VENUE_EXTRA),
  };
}

function vendorToEntry(v, contactRows) {
  const contacts = contactsFromRows(contactRows, v.contact);
  return {
    id: v.id,
    type: 'vendor',
    name: v.name || '',
    category: v.category || '',
    contact: contacts[0] ? contacts[0].name : '',
    contacts,
    phone: v.phone || '',
    email: v.email || '',
    street: v.address_line1 || '',
    city: v.city || '',
    state: v.state || '',
    country: v.country || COUNTRY_DEFAULT,
    website: v.website || '',
    location: v.location || [v.city, v.state].filter(Boolean).join(', '),
    notes: v.notes || '',
    rating: v.rating || 'unrated',
    projects: [],
    ...extraFromRow(v, VENDOR_EXTRA),
  };
}

// Loads every organisations/venues/vendors row and flattens them into the app's
// existing single `database` array shape, mixed-type just like the old
// localStorage blob was.
export async function loadDatabaseEntries() {
  const [orgsRes, contactsRes, venuesRes, vendorsRes] = await Promise.all([
    selectAllRes(() => supabase.from('organisations').select('*').order('id')),
    selectAllRes(() => supabase.from('contacts').select('*').eq('active', true).order('id')),
    selectAllRes(() => supabase.from('venues').select('*').order('id')),
    selectAllRes(() => supabase.from('vendors').select('*').order('id')),
  ]);
  if (orgsRes.error) throw orgsRes.error;
  if (contactsRes.error) throw contactsRes.error;
  if (venuesRes.error) throw venuesRes.error;
  if (vendorsRes.error) throw vendorsRes.error;

  // v7.97: every active contact, the primary one first, then in the order they were added
  // (v7.99: for venues and vendors too)
  const byOwner = { organisation_id: new Map(), venue_id: new Map(), vendor_id: new Map() };
  (contactsRes.data || []).forEach(c => {
    Object.keys(byOwner).forEach(col => { const id = c[col]; if (!id) return; const m = byOwner[col]; if (!m.has(id)) m.set(id, []); m.get(id).push(c); });
  });
  Object.values(byOwner).forEach(m => m.forEach(list => list.sort((a, b) => (a.role_type === 'primary' ? 0 : 1) - (b.role_type === 'primary' ? 0 : 1)
    || String(a.created_at || '').localeCompare(String(b.created_at || '')) || String(a.id).localeCompare(String(b.id)))));
  const clients = (orgsRes.data || []).map(org => orgToEntry(org, byOwner.organisation_id.get(org.id)));
  const venues = (venuesRes.data || []).map(v => venueToEntry(v, byOwner.venue_id.get(v.id)));
  const vendors = (vendorsRes.data || []).map(v => vendorToEntry(v, byOwner.vendor_id.get(v.id)));
  return [...clients, ...venues, ...vendors];
}

// v7.97: writes a record's whole contact list (v7.99: a venue's or vendor's too, by ownerCol). The first is the primary contact; one already on
// file keeps its own role (lead planner and so on); a contact taken off the list is marked
// inactive (never deleted), so nothing imported is lost. retire is false when the caller sent no
// list at all (an older shape of entry): then nothing already on file is touched.
async function syncContacts(ownerCol, ownerId, entry, retire = true) {
  const list = Array.isArray(entry.contacts) ? entry.contacts : [];
  const { data: existing, error: findErr } = await supabase
    .from('contacts')
    .select('id,role_type,active,name')
    .eq(ownerCol, ownerId);
  if (findErr) throw findErr;
  const byId = new Map((existing || []).map(c => [c.id, c]));
  const keep = new Set();
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    // a contact new to this list but already on file under the same name is that row
    if (!byId.has(c.id)) {
      const same = (existing || []).find(x => !keep.has(x.id) && !list.some(y => y.id === x.id) && sameName(x.name, c.name) && c.name);
      if (same) c.id = same.id;
    }
    const ex = byId.get(c.id);
    const row = {
      [ownerCol]: ownerId,
      name: c.name || NO_NAME,
      title: c.title || null,
      email: c.email || null,
      phone: c.phone || null,
      active: true,
    };
    if (i === 0) row.role_type = 'primary';
    else if (!ex || !ex.role_type || ex.role_type === 'primary') row.role_type = 'other';
    if (ex) {
      const { error } = await supabase.from('contacts').update(row).eq('id', c.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from('contacts').insert({ id: c.id, ...row });
      if (error) throw error;
    }
    keep.add(c.id);
  }
  const gone = retire ? (existing || []).filter(c => c.active !== false && !keep.has(c.id)).map(c => c.id) : [];
  if (gone.length) {
    const { error } = await supabase.from('contacts').update({ active: false }).in('id', gone);
    if (error) throw error;
  }
}

const syncClientContacts = (orgId, entry, retire = true) => syncContacts('organisation_id', orgId, entry, retire);
// v7.99: a venue's or vendor's list. Until 20261020000001 has been run there is no venue_id or
// vendor_id column; the record itself is saved and the list is left out.
const missingContactsColumn = err => !!err && (err.code === 'PGRST204' || err.code === '42703' || err.code === 'PGRST100') && /venue_id|vendor_id/i.test(String(err.message || ''));
async function syncPlaceContacts(entry, retire) {
  try { await syncContacts(OWNER_COL[entry.type], entry.id, entry, retire); }
  catch (err) { if (missingContactsColumn(err)) { console.warn('Contacts list not saved: run 20261020000001_venue_vendor_contacts_categories.sql'); return; } throw err; }
}

// v7.97: until 20261019000001_client_contacts_main_phone.sql has been run, organisations has no
// phone or email column; the save is then repeated without them rather than failing.
// v7.109: the same for the usual Senior Manager and Finance columns (20261023000003).
const missingOrgColumn = err => !!err && (err.code === 'PGRST204' || err.code === '42703') && /phone|email/i.test(String(err.message || ''));
const missingUsualColumn = err => !!err && (err.code === 'PGRST204' || err.code === '42703') && /usual_(admin|finance)_id/i.test(String(err.message || ''));
async function writeOrg(op, entry) {
  let row = clientRow(entry);
  let { error } = await op(row);
  if (missingUsualColumn(error)) {
    const { usual_admin_id, usual_finance_id, ...rest } = row;
    row = rest;
    ({ error } = await op(row));
  }
  if (missingOrgColumn(error)) {
    const { phone, email, ...rest } = row;
    ({ error } = await op(rest));
  }
  if (error) throw error;
}

// entry must already carry a real UUID id (see the module note above).
export async function dbCreate(entry0) {
  const entry = cleanDbEntry(entry0);
  if (entry.type === 'client') {
    await writeOrg(row => supabase.from('organisations').insert({ id: entry.id, ...row }), entry);
    await syncClientContacts(entry.id, entry);
  } else if (entry.type === 'venue') {
    const { error } = await supabase.from('venues').insert({ id: entry.id, ...venueVendorRow(entry), ...extraToRow(entry, VENUE_EXTRA) });
    if (error) throw error;
    await syncPlaceContacts(entry, true);
  } else if (entry.type === 'vendor') {
    const { error } = await supabase.from('vendors').insert({ id: entry.id, ...venueVendorRow(entry), ...extraToRow(entry, VENDOR_EXTRA) });
    if (error) throw error;
    await syncPlaceContacts(entry, true);
  } else {
    throw new Error('dbCreate: unknown database entry type "' + entry.type + '"');
  }
}

export async function dbUpdate(entry0) {
  const entry = cleanDbEntry(entry0);
  if (entry.type === 'client') {
    await writeOrg(row => supabase.from('organisations').update(row).eq('id', entry.id), entry);
    await syncClientContacts(entry.id, entry, Array.isArray(entry0.contacts));
  } else if (entry.type === 'venue') {
    const { error } = await supabase.from('venues').update({ ...venueVendorRow(entry), ...extraToRow(entry, VENUE_EXTRA) }).eq('id', entry.id);
    if (error) throw error;
    await syncPlaceContacts(entry, Array.isArray(entry0.contacts));
  } else if (entry.type === 'vendor') {
    const { error } = await supabase.from('vendors').update({ ...venueVendorRow(entry), ...extraToRow(entry, VENDOR_EXTRA) }).eq('id', entry.id);
    if (error) throw error;
    await syncPlaceContacts(entry, Array.isArray(entry0.contacts));
  } else {
    throw new Error('dbUpdate: unknown database entry type "' + entry.type + '"');
  }
}

export async function dbDelete(id, type) {
  const table = type === 'client' ? 'organisations' : type === 'venue' ? 'venues' : type === 'vendor' ? 'vendors' : null;
  if (!table) throw new Error('dbDelete: unknown database entry type "' + type + '"');
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}
